import { QueryFunction, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { ApiClient } from '@dozuki/web-js/utils';
import { ErrorMessages } from '~/monolith-api';
import { RegisteredQueryKeys } from './RegisteredQueryKeys';
import { useApiErrorMessageToast } from './useApiErrorMessageToast';
import { useRouter } from 'next/router';

export type UseRegisteredQueryOptions<TData, TError> = UseQueryOptions<
   TData,
   TError,
   TData,
   [RegisteredQueryKeys, ...any]
> & {
   errorMessages?: ErrorMessages;
};

/**
 * useRegisteredQuery
 * @deprecated - Please see ~/pages/Courses for an example of how to model queries and invalidation
 * @description - Used in place of 'useQuery'. Enforces a query's key to be
 *    listed in "RegisteredQueryKeys"
 *
 * @usage -
 *  useRegisteredQuery(key: RegisteredQueryKeys, quernFn, options)
 */
export function useRegisteredQuery<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
   // Expressing the full type definition here for better type hinting
   key: [RegisteredQueryKeys, ...any],
   queryFunction: QueryFunction<TData, [RegisteredQueryKeys, ...any]>,
   options: UseRegisteredQueryOptions<TData, TError> = {}
): UseQueryResult<TData, TError> {
   const apiErrorMessageToast = useApiErrorMessageToast(options.errorMessages);
   const router = useRouter();

   return useQuery(key, queryFunction, {
      ...options,
      onError(error, ...args) {
         if (ApiClient.isInvalidLoginError(error)) {
            router.push('/Login');
         }
         if (!ApiClient.shouldRedirectError(error)) {
            apiErrorMessageToast.handle(error);
         }
         if (options.onError) {
            options.onError(error, ...args);
         }
      },
      useErrorBoundary: ApiClient.shouldRedirectError,
   });
}
